"use client"

import * as React from "react"
import { CheckIcon, CopyIcon } from "lucide-react"

import { cn } from "../../lib/utils"
import { Button } from "./button"

const DEFAULT_SUCCESS_MS = 1600

/** Copy ↔ check swap used by `CopyButton` and `CopyableCode`. */
export function CopyIconAnimated({
  copied,
  className,
  iconClassName = "size-4",
}: {
  copied: boolean
  /** Box for the stacked icons (default `size-4`) */
  className?: string
  /** Passed to both icons, e.g. `size-3.5` for dense rows */
  iconClassName?: string
}) {
  return (
    <span
      className={cn("relative inline-flex size-4 shrink-0 items-center justify-center overflow-hidden", className)}
      aria-hidden
    >
      <span
        className={cn(
          "pointer-events-none absolute inset-0 flex items-center justify-center transition-all duration-200 ease-out",
          copied ? "scale-50 opacity-0" : "scale-100 opacity-100",
        )}
      >
        <CopyIcon className={cn(iconClassName)} />
      </span>
      <span
        className={cn(
          "pointer-events-none absolute inset-0 flex items-center justify-center text-emerald-600 transition-all duration-200 ease-out dark:text-emerald-400",
          copied ? "scale-100 opacity-100" : "scale-50 opacity-0",
        )}
      >
        <CheckIcon className={cn(iconClassName)} strokeWidth={2.5} />
      </span>
    </span>
  )
}

export type CopyButtonProps = Omit<
  React.ComponentProps<typeof Button>,
  "onClick" | "asChild" | "children" | "onCopy"
> & {
  /** String written to the clipboard */
  value: string
  /** Visible label (and default accessible name) */
  children: React.ReactNode
  onCopy?: (success: boolean) => void
  successDurationMs?: number
  /** Icon before or after the label */
  iconPosition?: "start" | "end"
  /** Icon size utility on both icons (default matches inline code copy control) */
  copyIconClassName?: string
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}

export function CopyButton({
  value,
  children,
  onCopy,
  successDurationMs = DEFAULT_SUCCESS_MS,
  iconPosition = "end",
  disabled,
  onClick,
  className,
  copyIconClassName,
  ...rest
}: CopyButtonProps) {
  const [copied, setCopied] = React.useState(false)
  const timeoutRef = React.useRef<number | undefined>(undefined)

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current !== undefined) window.clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(event)
    if (event.defaultPrevented || disabled || !value) return

    try {
      await navigator.clipboard.writeText(value)
      onCopy?.(true)
      setCopied(true)
      if (timeoutRef.current !== undefined) window.clearTimeout(timeoutRef.current)
      timeoutRef.current = window.setTimeout(() => setCopied(false), successDurationMs)
    } catch {
      onCopy?.(false)
      setCopied(false)
    }
  }

  return (
    <Button
      {...rest}
      type='button'
      disabled={disabled}
      className={cn(className)}
      onClick={handleClick}
    >
      {iconPosition === "start" ? (
        <>
          <span data-icon='inline-start' className='inline-flex'>
            <CopyIconAnimated
              copied={copied}
              className={copyIconClassName}
              iconClassName={copyIconClassName ?? "size-4"}
            />
          </span>
          {children}
        </>
      ) : (
        <>
          {children}
          <span data-icon='inline-end' className='inline-flex'>
            <CopyIconAnimated
              copied={copied}
              className={copyIconClassName}
              iconClassName={copyIconClassName ?? "size-4"}
            />
          </span>
        </>
      )}
    </Button>
  )
}
